<template>
  <ModalStep
    id="users-import-confirmation-modal-content"
    :header="{
      title: t('admin.users.excelConfirmationModal.title'),
    }"
  >
    <template #bodyContent>
      <div v-if="usersRef">
        <div class="users-import-table">
          <table class="table">
            <thead>
              <tr>
                <th v-for="header in tableHeaderRowsRef" :key="header.key">
                  {{ header.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(user, index) in formattedUsers" :key="user.email" :class="{ 'row-alt': index % 2 !== 0 }">
                <td data-test-id="table-row_element" v-for="header in tableHeaderRowsRef" :key="header.key">
                  {{ user[header.key] ?? '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="c-btns-container">
        <CustomButton usage="button" type="submit" color="white" :text="t('ui.button.back')" @emit:click="onPrev" />
        <CustomButton
          :is-loading="isImportAdminUsersPendingRef"
          usage="button"
          type="submit"
          color="primary"
          :text="t('ui.button.confirm')"
          @emit:click="onConfirm"
          data-test-id="admin-users-import-confirm"
        >
        </CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed } from 'vue'

import { importAdminUsersMutation } from '@/features/admin/users/api'
import usePrograms from '@/features/programs/hooks/use-programs.hook'
import {
  selectGetSelectedPeriodId,
  selectGetSelectedProgramId,
} from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import { selectGetAdminUsersImportModal } from '@/features/ui/store/modal/modal.selectors'
import type { AdminUsersImportModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'
import { invalidateAdminUsers } from '@/utils/lib/vue-query'

const toast = useToast()
const { t } = useI18n()

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const selectedPeriodIdRef = selectGetSelectedPeriodId(programsStore)

const adminUsersImportModalRef = selectGetAdminUsersImportModal(modalsStore)

const { programsRef } = usePrograms()

const usersRef = computed(() => adminUsersImportModalRef.value.attachedData.users) as any // any because of the html code

const formattedUsers: any[] = []
const fixedHeaders = ['email', 'firstname', 'lastname', 'role', 'phone', 'sso', 'language', 'programid', 'description']

for (const user of usersRef.value) {
  const formattedUser: any = {}

  for (const field of user.fields) {
    let fieldValue = field.value

    if (fieldValue && typeof fieldValue === 'object' && 'uuid' in fieldValue) {
      fieldValue = fieldValue.value
    }

    if (field.key.toLowerCase() === 'programid') {
      const programName = programsRef.value.find((program) => program.id === fieldValue)
      formattedUser[field.key] = programName ? programName.name : fieldValue
    } else if (field.key.toLowerCase() === 'sso') {
      formattedUser[field.key] = fieldValue ? 'Oui' : 'Non'
    } else {
      formattedUser[field.key] = fieldValue
    }
  }

  formattedUsers.push(formattedUser)
}

const tableHeaderRowsRef = usersRef.value[0].fields.map((field: { key: string }) => {
  const key = field.key
  const label = fixedHeaders.includes(field.key.toLowerCase())
    ? t(`admin.users.excelConfirmationModal.key.${field.key}`)
    : field.key.charAt(0).toUpperCase() + field.key.slice(1)

  return { key, label }
})

const { mutate: importAdminUsers, isPending: isImportAdminUsersPendingRef } = useMutation({
  mutationFn: importAdminUsersMutation,
  onError: () => {
    toast?.error(t('error.common'))
  },
  onSuccess: async () => {
    await invalidateAdminUsers(selectedProgramIdRef.value!)
    toast?.success(t('admin.users.excelConfirmationModal.success'))
    modalsStore.toggleModal('admin-users-import', false)
  },
})

async function formatUsersForBackend(usersToImport: any[]): Promise<any[]> {
  return usersToImport.map((user) => {
    // Format de l'utilisateur
    const formattedUser = {
      email: '',
      firstname: '',
      lastname: '',
      roles: '',
      phone: '',
      sso: false,
      language: '',
      description: '',
      matchingCriteria: [] as { inputId: string; value: string }[],
      menteeMatchingCriteria: [] as { inputId: string; value: string }[],
      programId: selectedProgramIdRef.value!,
    }

    // On va itérer sur les champs pour les ajouter dans le bon format
    user.fields.forEach((field: { key: string; value: any }) => {
      const { key, value } = field

      switch (key.toLowerCase()) {
        case 'email':
          formattedUser.email = value
          break
        case 'firstname':
          formattedUser.firstname = value
          break
        case 'lastname':
          formattedUser.lastname = value
          break
        case 'roles':
          formattedUser.roles = value
          break
        case 'phone':
          formattedUser.phone = value
          break
        case 'sso':
          formattedUser.sso = value === true || value === 'true' // On normalise le SSO
          break
        case 'language':
          formattedUser.language = value
          break
        case 'description':
          formattedUser.description = value
          break
        default:
          if (value && value.uuid) {
            // Si le criteriaType est défini, on l'utilise pour déterminer où placer le critère
            // On envoie aussi les valeurs vides pour permettre la suppression côté backend
            if (value.criteriaType === 'mentee') {
              if (formattedUser.roles.includes('MENTEE')) {
                formattedUser.menteeMatchingCriteria.push({
                  inputId: value.uuid,
                  value: value.value || '',
                })
              }
            } else {
              if (formattedUser.roles.includes('MENTOR')) {
                formattedUser.matchingCriteria.push({
                  inputId: value.uuid,
                  value: value.value || '',
                })
              }
            }
          }
          break
      }
    })

    return formattedUser
  })
}

async function onConfirm() {
  const formattedUsersPayload = await formatUsersForBackend(usersRef.value)
  importAdminUsers({
    programId: selectedProgramIdRef.value!,
    periodId: selectedPeriodIdRef.value!,
    users: formattedUsersPayload,
  })
}

function onPrev() {
  modalsStore.setModalStep<AdminUsersImportModalType>('admin-users-import', 'users-import')
}
</script>

<style lang="scss">
@import './users-import-confirmation-modal-content.scss';
</style>
